import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/numberFormatter';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const MySwal = withReactContent(Swal);

const ObservationRequestDetailModal = ({ observationId, requestId, customerId, request, onClose }) => {
  const [requestDetail, setRequestDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [observation, setObservation] = useState(null);
  const [replyingToNoteId, setReplyingToNoteId] = useState(null);
  const [noteContent, setNoteContent] = useState('');
  const [creatingNote, setCreatingNote] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Estados para archivos
  const [files, setFiles] = useState([]);
  const [fileCategories, setFileCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  useEffect(() => {
    if (request) {
      // Si ya tenemos los datos de la solicitud del listado, usarlos
      setObservation(request.observation || null);
      fetchRequestDetails();
    } else {
      fetchRequestDetails();
    }
    
    // Cargar categorías y archivos al abrir el modal
    if (observationId) {
      fetchFileCategories();
      fetchFiles();
    }
  }, [observationId, requestId, customerId]);

  const fetchRequestDetails = async () => {
    if (!observationId) {
      console.error('observationId es requerido');
      MySwal.fire({
        title: 'Error',
        text: 'ID de observación no proporcionado',
        icon: 'error'
      }).then(() => {
        onClose();
      });
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/observations/${observationId}`);
      setRequestDetail(response.data);
      
      // Si hay observación en la respuesta, establecerla
      if (response.data.observation) {
        setObservation(response.data.observation);
        // Las notas vienen incluidas en la observación
        if (response.data.observation.notes) {
          setNotes(response.data.observation.notes);
        }
      }
    } catch (error) {
      console.error('Error al obtener detalles de la solicitud:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron cargar los detalles de la solicitud',
        icon: 'error'
      }).then(() => {
        onClose();
      });
    } finally {
      setLoading(false);
    }
  };

  const refreshNotes = async () => {
    if (!observationId) {
      console.error('observationId es requerido');
      return;
    }

    try {
      const response = await api.get(`/observations/${observationId}`);
      if (response.data.observation && response.data.observation.notes) {
        setNotes(response.data.observation.notes);
      }
    } catch (error) {
      console.error('Error al recargar notas:', error);
    }
  };

  const refreshRequestDetails = async () => {
    if (!observationId) {
      return;
    }

    try {
      const response = await api.get(`/observations/${observationId}`);
      setRequestDetail(response.data);
      
      // Si hay observación en la respuesta, establecerla
      if (response.data.observation) {
        setObservation(response.data.observation);
        // Las notas vienen incluidas en la observación
        if (response.data.observation.notes) {
          setNotes(response.data.observation.notes);
        }
      }
    } catch (error) {
      console.error('Error al refrescar detalles de la solicitud:', error);
      throw error;
    }
  };

  const refreshAll = async () => {
    if (!observationId) {
      return;
    }

    setRefreshing(true);
    try {
      // Recargar todos los datos en paralelo
      await Promise.all([
        refreshRequestDetails(),
        fetchFiles()
      ]);

    } catch (error) {
      console.error('Error al refrescar datos:', error);
      MySwal.fire({
        title: 'Error',
        text: 'No se pudieron actualizar todos los datos',
        icon: 'error',
        timer: 2000
      });
    } finally {
      setRefreshing(false);
    }
  };

  const createNote = async (observationId, content) => {
    if (!content.trim()) {
      MySwal.fire({
        title: 'Error',
        text: 'El contenido de la nota es requerido',
        icon: 'error'
      });
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post('/notes', {
        noteable_type: 'observation',
        noteable_id: observationId,
        content: content.trim()
      });

      if (response.data.success) {
        MySwal.fire({
          title: 'Éxito',
          text: 'Nota creada correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setNoteContent('');
        setCreatingNote(false);
        await refreshNotes();
      } else {
        throw new Error(response.data.message || 'Error al crear la nota');
      }
    } catch (error) {
      console.error('Error al crear nota:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al crear la nota';
      MySwal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const replyToNote = async (noteId, content) => {
    if (!content.trim()) {
      MySwal.fire({
        title: 'Error',
        text: 'El contenido de la respuesta es requerido',
        icon: 'error'
      });
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.post(`/notes/${noteId}/reply`, {
        content: content.trim()
      });

      if (response.data.success) {
        MySwal.fire({
          title: 'Éxito',
          text: 'Respuesta creada correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        setNoteContent('');
        setReplyingToNoteId(null);
        await refreshNotes();
      } else {
        throw new Error(response.data.message || 'Error al crear la respuesta');
      }
    } catch (error) {
      console.error('Error al responder a nota:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al crear la respuesta';
      MySwal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const deleteNote = async (noteId) => {
    const result = await MySwal.fire({
      title: '¿Estás seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await api.delete(`/notes/${noteId}`);

      if (response.data.success) {
        MySwal.fire({
          title: 'Éxito',
          text: 'Nota eliminada correctamente',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        await refreshNotes();
      } else {
        throw new Error(response.data.message || 'Error al eliminar la nota');
      }
    } catch (error) {
      console.error('Error al eliminar nota:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al eliminar la nota';
      MySwal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Funciones para manejo de archivos
  const fetchFileCategories = async () => {
    try {
      const response = await api.get('/file-categories');
      setFileCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error al obtener categorías de archivo:', error);
    }
  };

  const fetchFiles = async () => {
    if (!observationId) {
      return;
    }

    try {
      const response = await api.get(`/observations/${observationId}/files`);
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error al obtener archivos:', error);
      setFiles([]);
    }
  };

  const validateFileType = (file) => {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
    
    // Validar por tipo MIME
    const fileType = file.type.toLowerCase();
    const isAllowedType = allowedTypes.some(type => fileType === type.toLowerCase());
    
    if (isAllowedType) {
      return true;
    }
    
    // Si el tipo MIME no coincide o no está disponible, validar por extensión
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    return hasValidExtension;
  };

  const handleFileSelect = (file) => {
    if (!file) {
      return;
    }

    // Validar tipo de archivo
    if (!validateFileType(file)) {
      MySwal.fire({
        title: 'Tipo de archivo no permitido',
        text: 'Solo se permiten archivos PDF, JPG, JPEG y PNG',
        icon: 'error',
        timer: 3000
      });
      return;
    }

    setPendingFile(file);
    setShowCategoryModal(true);
  };

  const handleCategoryConfirm = async (selectedCategories) => {
    if (!selectedCategories || selectedCategories.length === 0) {
      MySwal.fire({
        title: 'Error',
        text: 'Debe seleccionar al menos una categoría',
        icon: 'error'
      });
      return;
    }

    if (!pendingFile) {
      return;
    }

    setShowCategoryModal(false);
    await handleFileUpload(pendingFile, selectedCategories);
  };

  const handleFileUpload = async (file, categories) => {
    if (!observationId || !file || !categories || categories.length === 0) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Agregar categorías como array
      categories.forEach(category => {
        formData.append('categories[]', category);
      });

      const response = await api.post(
        `/observations/${observationId}/files/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      MySwal.fire({
        title: '¡Éxito!',
        text: 'Archivo subido correctamente',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });

      // Recargar lista de archivos
      await fetchFiles();
      
      // Limpiar estado
      setPendingFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error al subir archivo:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error al subir el archivo';
      MySwal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getObservationStatusBadge = (status) => {
    const statusMap = {
      'open': { class: 'bg-light-warning', label: 'Abierta' },
      'resolved': { class: 'bg-light-success', label: 'Resuelta' },
      'cancelled': { class: 'bg-light-secondary', label: 'Cancelada' }
    };
    return statusMap[status] || { class: 'bg-light-secondary', label: status };
  };

  const ObservationSkeleton = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-3 px-1">
        <Skeleton width={200} height={20} />
        <Skeleton width={80} height={25} />
      </div>
      <div className="card border shadow-sm mb-3">
        <div className="card-body p-3">
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <Skeleton width={100} height={12} />
              <Skeleton width={150} height={16} />
            </div>
            <div className="col-md-6">
              <Skeleton width={100} height={12} />
              <Skeleton width={150} height={16} />
            </div>
          </div>
          <div className="border-top pt-3 mt-3">
            <Skeleton width={100} height={12} />
            <Skeleton width="100%" height={60} />
          </div>
        </div>
      </div>
    </div>
  );

  const NotesSkeleton = () => (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 px-1">
        <Skeleton width={100} height={20} />
        <Skeleton width={120} height={30} />
      </div>
      <div className="border rounded p-3">
        <Skeleton width="100%" height={80} />
        <Skeleton width="100%" height={80} className="mt-2" />
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          zIndex: 1040,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }}
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          zIndex: 1050,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'auto'
        }} 
        tabIndex="-1"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" style={{ margin: '1.75rem auto' }}>
          <div className="modal-content">
            {/* Header */}
            <div className="modal-header">
              <h5 className="modal-title fw-bold">
                {loading ? 'Cargando...' : `Solicitud #${requestId || requestDetail?.id || 'N/A'}`}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Cerrar"
              ></button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {loading ? (
                <>
                  <ObservationSkeleton />
                  <NotesSkeleton />
                </>
              ) : (
                <>
                  {/* SECCIÓN INFORMACIÓN DE LA OBSERVACIÓN */}
                  {observation && (
                    <div className="mb-4">
                      <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                        <h6 className="mb-0 fw-bold">Información de la Observación</h6>
                        <span 
                          className={`badge ${getObservationStatusBadge(observation.status).class}`}
                        >
                          {getObservationStatusBadge(observation.status).label}
                        </span>
                      </div>
                      
                      {/* Card tipo tarjeta impresa */}
                      <div 
                        className="card border shadow-sm mb-3" 
                        style={{ 
                          background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)', 
                          border: '2px solid #dee2e6 !important' 
                        }}
                      >
                        <div className="card-body p-3">
                          <div className="row g-3 mb-3">
                            <div className="col-md-6">
                              <small 
                                className="text-muted d-block mb-1" 
                                style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                              >
                                CREADO POR
                              </small>
                              <p className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                                {observation.created_by || 'N/A'}
                              </p>
                            </div>
                            <div className="col-md-6">
                              <small 
                                className="text-muted d-block mb-1" 
                                style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                              >
                                FECHA DE CREACIÓN
                              </small>
                              <p className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                                {observation.created_at || 'N/A'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Información de resolución (se muestra dinámicamente) */}
                          {observation.resolved_by && observation.resolved_at && (
                            <div className="row g-3 mb-3">
                              <div className="col-12 col-md-6">
                                <small 
                                  className="text-muted d-block mb-1" 
                                  style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                >
                                  RESUELTO POR
                                </small>
                                <p className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                                  {observation.resolved_by}
                                </p>
                              </div>
                              <div className="col-12 col-md-6">
                                <small 
                                  className="text-muted d-block mb-1" 
                                  style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                >
                                  FECHA DE RESOLUCIÓN
                                </small>
                                <p className="mb-0 fw-bold" style={{ fontSize: '1rem' }}>
                                  {observation.resolved_at}
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {/* Descripción dentro del card */}
                          <div className="border-top pt-3 mt-3">
                            <small 
                              className="text-muted d-block mb-2" 
                              style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                            >
                              DESCRIPCIÓN
                            </small>
                            <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                              {observation.description || 'Sin descripción'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Divider si hay observación */}
                  {observation && <div className="perforated-divider"></div>}

                  {/* SECCIÓN NOTAS */}
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                      <h6 className="mb-0 fw-bold">Notas</h6>
                      {observation && (
                        <button 
                          type="button" 
                          className="btn btn-sm btn-light-primary"
                          onClick={() => setCreatingNote(!creatingNote)}
                          disabled={actionLoading}
                        >
                          <i className="bi bi-plus me-1"></i> Nueva Nota
                        </button>
                      )}
                    </div>
                    <div id="observation-notes-container">
                      {/* Formulario para crear nueva nota */}
                      {creatingNote && observation && (
                        <div className="card mb-3 border">
                          <div className="card-body p-3">
                            <div className="mb-2">
                              <label className="form-label small fw-bold">Nueva Nota</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={noteContent}
                                onChange={(e) => setNoteContent(e.target.value)}
                                placeholder="Escribe tu nota aquí..."
                                disabled={actionLoading}
                              />
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                type="button"
                                className="btn btn-sm btn-light-success"
                                onClick={() => createNote(observation.id, noteContent)}
                                disabled={actionLoading || !noteContent.trim()}
                              >
                                {actionLoading ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    Guardando...
                                  </>
                                ) : (
                                  <>
                                    <i className="bi bi-check me-1"></i> Guardar
                                  </>
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  setCreatingNote(false);
                                  setNoteContent('');
                                }}
                                disabled={actionLoading}
                              >
                                Cancelar
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {loading ? (
                        <div className="text-center text-muted py-2">
                          <i className="bi bi-arrow-repeat spin me-2"></i>
                          <small>Cargando notas...</small>
                        </div>
                      ) : !notes || notes.length === 0 ? (
                        <div className="bg-light border m-2 p-2 rounded">
                          <small className="text-muted">
                            <i className="bi bi-info-circle me-2"></i>
                            No hay notas registradas para esta observación.
                          </small>
                        </div>
                      ) : (
                        <div className="notes-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {notes.map((note) => (
                            <div key={note.id} className="card mb-2 border">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <strong className="d-block">{note.user?.name || 'Usuario'}</strong>
                                    <small className="text-muted">{note.date}</small>
                                  </div>
                                  <div className="d-flex gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light-primary"
                                      onClick={() => {
                                        setReplyingToNoteId(replyingToNoteId === note.id ? null : note.id);
                                        setNoteContent('');
                                      }}
                                      disabled={actionLoading}
                                      title="Responder"
                                    >
                                      <i className="bi bi-reply"></i>
                                    </button>
                                    {note.can_delete && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light-danger"
                                        onClick={() => deleteNote(note.id)}
                                        disabled={actionLoading}
                                        title="Eliminar"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                  {note.content}
                                </p>

                                {/* Formulario para responder */}
                                {replyingToNoteId === note.id && (
                                  <div className="mt-3 p-3 bg-light rounded">
                                    <div className="mb-2">
                                      <label className="form-label small fw-bold">Responder</label>
                                      <textarea
                                        className="form-control"
                                        rows="2"
                                        value={noteContent}
                                        onChange={(e) => setNoteContent(e.target.value)}
                                        placeholder="Escribe tu respuesta aquí..."
                                        disabled={actionLoading}
                                      />
                                    </div>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light-success"
                                        onClick={() => replyToNote(note.id, noteContent)}
                                        disabled={actionLoading || !noteContent.trim()}
                                      >
                                        {actionLoading ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Guardando...
                                          </>
                                        ) : (
                                          <>
                                            <i className="bi bi-check me-1"></i> Enviar
                                          </>
                                        )}
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => {
                                          setReplyingToNoteId(null);
                                          setNoteContent('');
                                        }}
                                        disabled={actionLoading}
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {/* Respuestas existentes */}
                                {note.replies && note.replies.length > 0 && (
                                  <div className="mt-3 ps-3 border-start border-2">
                                    {note.replies.map((reply) => (
                                      <div key={reply.id} className="mb-2">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                          <strong className="small">{reply.user?.name || 'Usuario'}</strong>
                                          <small className="text-muted">{reply.date}</small>
                                        </div>
                                        <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                                          {reply.content}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Divider si hay observación */}
                  {observation && <div className="perforated-divider"></div>}

                  {/* SECCIÓN ARCHIVOS */}
                  <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                      <h6 className="mb-0 fw-bold">Archivos</h6>
                    </div>

                    {/* Área de drag & drop */}
                    <div
                      className={`border rounded p-4 text-center ${dragActive ? 'border-primary bg-light' : ''}`}
                      style={{
                        borderStyle: dragActive ? 'dashed' : 'solid',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragActive(true);
                      }}
                      onDragLeave={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragActive(false);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragActive(false);
                        
                        const droppedFiles = e.dataTransfer.files;
                        if (droppedFiles && droppedFiles.length > 0) {
                          handleFileSelect(droppedFiles[0]);
                        }
                      }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';
                        input.onchange = (e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleFileSelect(e.target.files[0]);
                          }
                        };
                        input.click();
                      }}
                    >
                      {uploading ? (
                        <div>
                          <div className="spinner-border text-primary mb-0" role="status">
                            <span className="visually-hidden">Subiendo...</span>
                          </div>
                          <p className="mb-0 text-muted">
                            <small>Subiendo archivo... {uploadProgress}%</small>
                          </p>
                          {uploadProgress > 0 && (
                            <div className="progress mt-2" style={{ height: '6px' }}>
                              <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${uploadProgress}%` }}
                                aria-valuenow={uploadProgress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              ></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ padding: '0.25rem 0' }}>
                          <i className="bi bi-cloud-upload fs-2 text-muted mb-1"></i>
                          <p className="mb-0" style={{ lineHeight: 1.2 }}>
                            <strong>Arrastra un archivo aquí</strong>
                          </p>
                          <p className="mb-0 text-muted" style={{ lineHeight: 1, fontSize: '0.9em' }}>
                            <small>o haz clic para seleccionar</small>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Tabla de archivos */}
                    {files.length > 0 && (
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead>
                              <tr>
                                <th>Nombre</th>
                                <th>Categorías</th>
                                <th>Tamaño</th>
                                <th>Fecha</th>
                                <th>Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {files.map((file) => (
                                <tr key={file.id}>
                                  <td>
                                    <small className="text-truncate d-inline-block" style={{ maxWidth: '200px' }}>
                                      {file.name}
                                    </small>
                                  </td>
                                  <td>
                                    {file.categories && file.categories.length > 0 ? (
                                      <div className="d-flex flex-wrap gap-1">
                                        {file.categories.map((category) => (
                                          <span key={category.id} className="badge bg-light-secondary">
                                            {category.name}
                                          </span>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td>
                                    <small className="text-muted">{formatFileSize(file.size)}</small>
                                  </td>
                                  <td>
                                    <small className="text-muted">{file.created_at || '-'}</small>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-light-primary"
                                      onClick={() => {
                                        if (file.url) {
                                          window.open(file.url, '_blank');
                                        }
                                      }}
                                      title="Ver archivo"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Modal de categorías */}
                    {showCategoryModal && pendingFile && (
                      <div
                        className="modal fade show"
                        style={{ display: 'block', zIndex: 1060, backgroundColor: 'rgba(0,0,0,0.5)' }}
                        onClick={() => {
                          setShowCategoryModal(false);
                          setPendingFile(null);
                        }}
                      >
                        <div
                          className="modal-dialog modal-dialog-centered"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Seleccionar Categorías</h5>
                              <button
                                type="button"
                                className="btn-close"
                                onClick={() => {
                                  setShowCategoryModal(false);
                                  setPendingFile(null);
                                }}
                              ></button>
                            </div>
                            <div className="modal-body">
                              <div className="mb-3">
                                <p className="mb-1">
                                  <strong>Archivo:</strong> {pendingFile.name}
                                </p>
                                <p className="mb-0 text-muted">
                                  <small>Tamaño: {formatFileSize(pendingFile.size)}</small>
                                </p>
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  Categorías <span className="text-danger">*</span>
                                </label>
                                <select
                                  className="form-select"
                                  multiple
                                  size="8"
                                  id="categorySelect"
                                  onChange={(e) => {
                                    // El valor se obtiene del evento
                                  }}
                                >
                                  {fileCategories.map((category) => (
                                    <option key={category.id} value={category.alias}>
                                      {category.name}
                                    </option>
                                  ))}
                                </select>
                                <small className="text-muted">
                                  Mantén presionado Ctrl (o Cmd en Mac) para seleccionar múltiples categorías
                                </small>
                              </div>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => {
                                  setShowCategoryModal(false);
                                  setPendingFile(null);
                                }}
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                className="btn btn-light-success"
                                onClick={() => {
                                  const select = document.getElementById('categorySelect');
                                  const selectedOptions = Array.from(select.selectedOptions);
                                  const selectedCategories = selectedOptions.map(option => option.value);
                                  
                                  if (selectedCategories.length === 0) {
                                    MySwal.fire({
                                      title: 'Error',
                                      text: 'Debe seleccionar al menos una categoría',
                                      icon: 'error'
                                    });
                                    return;
                                  }
                                  
                                  handleCategoryConfirm(selectedCategories);
                                }}
                              >
                                Confirmar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="modal-footer justify-content-between">
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={onClose}
              >
                Cerrar
              </button>
              <div className="d-flex gap-2">
                <button 
                  type="button" 
                  className="btn btn-light-primary" 
                  onClick={refreshAll}
                  disabled={refreshing || loading}
                  title="Actualizar información"
                >
                  {refreshing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-repeat me-1"></i>
                      Actualizar
                    </>
                  )}
                </button>
                {/* Botones de acción se agregarán aquí cuando se definan */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Estilos para el modal
const styles = `
  .perforated-divider {
    position: relative;
    height: 1px;
    margin: 1.5rem 0;
    background: repeating-linear-gradient(
      to right,
      #6c757d 0px,
      #6c757d 12px,
      transparent 12px,
      transparent 20px
    );
    border: none;
    overflow: visible;
  }

  .perforated-divider::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #adb5bd 0px,
      #adb5bd 12px,
      transparent 12px,
      transparent 20px
    );
  }

  .perforated-divider::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 1px;
    background: repeating-linear-gradient(
      to right,
      #adb5bd 0px,
      #adb5bd 12px,
      transparent 12px,
      transparent 20px
    );
  }

  .bi-arrow-repeat.spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Inyectar estilos solo una vez
if (!document.getElementById('observation-request-detail-modal-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'observation-request-detail-modal-styles';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default ObservationRequestDetailModal;

